// Usage metering, budgets and reports (PRD §17).
import { z } from 'zod';
import * as U from './api-usage.js';
import * as PB from './api-prospect-budget.js';
import { OkSchema } from './api.js';
import { IdParam, items, route, W, Ws } from './route-def.js';

/** CSV body: streamed text/csv attachment; errors still use the JSON envelope. */
const Csv = z.string();

export const usageRoutes = {
  getReport: route({
    method: 'GET', path: '/workspaces/:workspaceId/reports', summary: 'Reporte P0: embudo, tiempos, llamadas, citas, atribución y fallas (sin contactos de prueba)', tag: 'reports',
    minRole: 'viewer', params: Ws, query: U.ReportQuerySchema, response: U.ReportSchema,
  }),
  exportReportCsv: route({
    method: 'GET', path: '/workspaces/:workspaceId/reports/export', summary: 'Exportar una tabla del reporte en CSV (auditado)', tag: 'reports',
    minRole: 'manager', params: Ws, query: U.ReportExportQuerySchema, response: Csv, produces: 'text/csv',
  }),
  getReportSettings: route({
    method: 'GET', path: '/workspaces/:workspaceId/reports/settings', summary: 'Definiciones configurables del reporte', tag: 'reports',
    minRole: 'viewer', params: Ws, response: U.ReportSettingsSchema,
  }),
  updateReportSettings: route({
    method: 'PUT', path: '/workspaces/:workspaceId/reports/settings', summary: 'Cambiar etapas que cuentan como calificadas (auditado)', tag: 'reports',
    minRole: 'admin', params: Ws, body: U.ReportSettingsSchema, response: U.ReportSettingsSchema,
  }),
  usageSummary: route({
    method: 'GET', path: '/workspaces/:workspaceId/usage/summary', summary: 'Uso del periodo por medidor, proyecto, medido/estimado y quién paga', tag: 'usage',
    minRole: 'manager', params: Ws, query: U.UsageSummaryQuerySchema, response: U.UsageSummarySchema,
  }),
  usageSeries: route({
    method: 'GET', path: '/workspaces/:workspaceId/usage/series', summary: 'Serie diaria de uso (30 o 90 días, zona horaria del espacio)', tag: 'usage',
    minRole: 'manager', params: Ws, query: U.UsageSeriesQuerySchema, response: U.UsageSeriesSchema,
  }),
  listUsageBudgets: route({
    method: 'GET', path: '/workspaces/:workspaceId/usage/budgets', summary: 'Límites y avisos con el consumo del periodo actual', tag: 'usage',
    minRole: 'admin', params: Ws, response: U.UsageBudgetListSchema,
  }),
  upsertUsageBudget: route({
    method: 'PUT', path: '/workspaces/:workspaceId/usage/budgets', summary: 'Crear o cambiar un límite mensual (propietarios; auditado)', tag: 'usage',
    minRole: 'owner', params: Ws, body: U.UpsertUsageBudgetSchema, response: U.UsageBudgetSchema,
  }),
  deleteUsageBudget: route({
    method: 'DELETE', path: '/workspaces/:workspaceId/usage/budgets/:budgetId', summary: 'Eliminar un límite (vuelve al predeterminado si existe; auditado)', tag: 'usage',
    minRole: 'owner', params: W({ budgetId: IdParam }), response: OkSchema,
  }),
  // Setter §06.10.5: accumulated AI/telephony budget per prospect (per project; estimates; owner-pending amount).
  getProspectBudgetConfig: route({
    method: 'GET', path: '/workspaces/:workspaceId/projects/:projectId/prospect-budget', summary: 'Presupuesto de IA por prospecto del proyecto (montos estimados; sin definir hasta que se fije)', tag: 'projects',
    minRole: 'advisor', module: null, params: W({ projectId: IdParam }), response: PB.ProspectBudgetConfigSchema,
  }),
  putProspectBudgetConfig: route({
    method: 'PUT', path: '/workspaces/:workspaceId/projects/:projectId/prospect-budget', summary: 'Guardar el presupuesto de IA por prospecto y las tarifas estimadas del proyecto (auditado)', tag: 'projects',
    minRole: 'admin', module: null, params: W({ projectId: IdParam }), body: PB.PutProspectBudgetConfigSchema, response: PB.ProspectBudgetConfigSchema,
  }),
  getLeadAiCost: route({
    method: 'GET', path: '/workspaces/:workspaceId/leads/:interestId/ai-cost', summary: 'Costo estimado acumulado de IA y telefonía de una oportunidad', tag: 'leads',
    minRole: 'advisor', module: null, params: W({ interestId: IdParam }), response: PB.LeadAiCostSchema,
  }),
  listProspectBudgetReviews: route({
    method: 'GET', path: '/workspaces/:workspaceId/prospect-budget/reviews', summary: 'Oportunidades pausadas o en revisión por presupuesto de IA', tag: 'leads',
    minRole: 'manager', module: null, params: Ws, query: PB.ProspectBudgetReviewQuerySchema, response: items(PB.ProspectBudgetReviewSchema),
  }),
  decideProspectBudgetReview: route({
    method: 'POST', path: '/workspaces/:workspaceId/prospect-budget/reviews/:reviewId/decision', summary: 'Registrar la decisión humana: aumentar el tope una vez, continuar o detener (auditado)', tag: 'leads',
    minRole: 'manager', module: null, params: W({ reviewId: IdParam }), body: PB.DecideProspectBudgetSchema, response: PB.ProspectBudgetReviewSchema,
  }),
} as const;
